import { Injectable } from '@angular/core';

const COOKIE_PREFIX = 'jepto_viewer_jwt_';

export interface ViewerAuthSession {
	email: string;
	reportId: string;
	jwt: string;
}

/**
 * Stores the wrapper JWT from `verifyViewShareCode` for PRIVATE share links.
 * Does not reconfigure Amplify Auth (guest IAM / Events stay intact).
 */
@Injectable({ providedIn: 'root' })
export class ViewerAuthService {
	private memory = new Map<string, ViewerAuthSession>();

	getJwtForReport(reportId: string): string | null {
		const key = reportId.toLowerCase();
		const cached = this.memory.get(key);
		if (cached?.jwt) {
			return cached.jwt;
		}
		const fromCookie = this.readCookie(COOKIE_PREFIX + key);
		if (fromCookie) {
			this.memory.set(key, {
				email: '',
				reportId: key,
				jwt: fromCookie,
			});
			return fromCookie;
		}
		return null;
	}

	/** False when missing, expired, or cleared from storage. */
	hasValidJwtForReport(reportId: string): boolean {
		const key = reportId.toLowerCase();
		const jwt = this.getJwtForReport(key);
		if (!jwt) {
			return false;
		}
		if (this.#isJwtExpired(jwt)) {
			this.clearJwtForReport(key);
			return false;
		}
		return true;
	}

	clearJwtForReport(reportId: string): void {
		const key = reportId.toLowerCase();
		this.memory.delete(key);
		this.writeCookie(COOKIE_PREFIX + key, '', 0);
	}

	storeViewerSession(
		email: string,
		reportId: string,
		jwt: string
	): ViewerAuthSession {
		const key = reportId.trim().toLowerCase();
		const authSession: ViewerAuthSession = {
			email: email.trim().toLowerCase(),
			reportId: key,
			jwt,
		};
		this.memory.set(key, authSession);
		this.writeCookie(COOKIE_PREFIX + key, jwt, 60 * 60 * 24 * 7);
		return authSession;
	}

	#isJwtExpired(jwt: string): boolean {
		const parts = jwt.split('.');
		if (parts.length !== 3) {
			return false;
		}
		try {
			const payload = JSON.parse(
				atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
			) as { exp?: number };
			if (typeof payload.exp !== 'number') {
				return false;
			}
			return payload.exp * 1000 <= Date.now();
		} catch {
			return false;
		}
	}

	private readCookie(name: string): string | null {
		if (typeof document === 'undefined') {
			return null;
		}
		const match = document.cookie
			.split(';')
			.map(part => part.trim())
			.find(part => part.startsWith(`${name}=`));
		if (!match) {
			return null;
		}
		return decodeURIComponent(match.slice(name.length + 1));
	}

	private writeCookie(
		name: string,
		value: string,
		maxAgeSeconds: number
	): void {
		if (typeof document === 'undefined') {
			return;
		}
		const secure = location.protocol === 'https:' ? '; Secure' : '';
		document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
	}
}
